// MACHINERY V2 (Stroje) — V3 table header row (M2.13).
//
// Reference impl: desktop/machinery-v3-table.jsx (SortableTh). The active sort
// column paints in `--mod-accent` with an ↑/↓ glyph; inactive sortable columns
// carry ↕, exactly as the artboard draws it.
//
// COLUMNS COME FROM ONE TABLE (fleetTableSort.FLEET_TABLE_COLUMNS), which is the
// same table the comparator reads — a column cannot be offered as sortable here
// but unhandled there. Columns with `sort: null` (AKTUÁLNE, 30 DNÍ) render as
// plain labels with NO affordance; the reasoning, including the deviation from
// the artboard's glyph, is recorded in fleetTableSort.ts's header.
//
// The <th> keeps the sheet's border + typography; when the column is sortable
// the BUTTON takes over the padding and fills the cell, so the whole header cell
// is clickable (`.has-sort` in machinery-v2-table.scss).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  FLEET_TABLE_COLUMNS,
  FLEET_TABLE_KEBAB_WIDTH,
  FleetTableSort,
  FleetTableSortColumn,
} from "./fleetTableSort";

export interface FleetTableHeadProps {
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
}

const alignClass = (align?: "left" | "center" | "right"): string =>
  align === "right" ? " is-right" : align === "center" ? " is-center" : "";

const FleetTableHead: React.FC<FleetTableHeadProps> = ({ sort, onSortChange }) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <thead>
      <tr>
        {FLEET_TABLE_COLUMNS.map((c) => {
          const active = !!c.sort && sort.column === c.sort;
          const label = translate(c.labelKey);
          return (
            <th
              key={c.key}
              scope="col"
              className={(c.sort ? "has-sort" : "") + alignClass(c.align)}
              style={{ width: c.width, textAlign: c.align || "left" }}
              aria-sort={
                active ? (sort.direction === "asc" ? "ascending" : "descending") : undefined
              }
            >
              {c.sort ? (
                <button
                  type="button"
                  className={"ms-sort" + (active ? " is-active" : "")}
                  onClick={() => onSortChange(c.sort as FleetTableSortColumn)}
                  title={label}
                >
                  <span>{label}</span>
                  <span style={{ fontSize: 8, opacity: 0.7 }} aria-hidden>
                    {active ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}
                  </span>
                </button>
              ) : (
                label
              )}
            </th>
          );
        })}
        <th style={{ width: FLEET_TABLE_KEBAB_WIDTH }} />
      </tr>
    </thead>
  );
};

export default FleetTableHead;
